import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useUserProfileStore } from '@entities/user';
import { useInstitutionStore } from '@entities/institution';
import type { CheckoutForm } from '@entities/order';

export const useCheckoutFormStore = defineStore('checkoutForm', () => {
  const storeUserProfile = useUserProfileStore();
  const storeInstitution = useInstitutionStore();

  const OrderForm = ref<CheckoutForm>({
    comment: '',
    cutlery: 0,
    payment_method: 'card',
    method_receipt: 'delivery',
    table_id: 0,
    delivery_address: storeUserProfile.selectedDeliveryAddress
      ? { address: storeUserProfile.selectedDeliveryAddress.address, zona: storeUserProfile.selectedDeliveryAddress.zona == null ? undefined : String(storeUserProfile.selectedDeliveryAddress.zona) }
      : null,
    delivery_zone_id: storeUserProfile.selectedDeliveryAddress?.zona == null ? null : String(storeUserProfile.selectedDeliveryAddress.zona),
    pointsToUse: 0,
    promocodeId: null,
    phone: '',
    name: '',
    preorder_mode: 'asap',
    reservation_time: null,
  });

  const orderId = ref('');

  // Координаты подтверждённого адреса доставки — нужны, чтобы при гонке
  // (филиал перегрузился между подбором и оформлением) повторно подобрать
  // филиал авто-роутером. Не часть CheckoutForm (не уходит в заказ напрямую).
  const deliveryCoords = ref<{ latitude: number; longitude: number } | null>(null);
  const setDeliveryCoords = (coords: { latitude: number; longitude: number } | null) => {
    deliveryCoords.value = coords;
  };

  // Сменилась строка выбранного адреса — точка прежнего адреса больше не действует.
  // Адрес меняют и вне окна карты («Мои адреса», шторка филиала, восстановление из
  // хранилища), а без сброса в заказ уезжала пара «строка нового адреса + координаты
  // старого»: заказ уходил не в ту зону. Окно карты пишет адрес раньше координат,
  // поэтому свежую точку этот сброс не задевает.
  watch(
    () => storeUserProfile.selectedDeliveryAddress?.address,
    (next, previous) => {
      if (previous !== undefined && next !== previous) deliveryCoords.value = null;
    },
  );

  // 🧠 Вычисляемые значения — автоматически обновятся
  const name = computed(() => storeUserProfile.getUser?.first_name || '');
  const phone = computed(() => storeUserProfile.getUser?.default_phone_number || '');
  const addressInstitution = computed(() => storeInstitution.selectedBranch?.address || '');

  const getOrderId = computed(() => orderId.value);

  // 🔧 Получить полный form (для отправки)
  const getForm = computed(() => ({
    ...OrderForm.value,
    name: name.value,
    phone: phone.value,
    address_institution: addressInstitution.value,
  }));

  const setOrderId = (id) => {
    orderId.value = id;
  };

  // ✏️ Обновить весь form
  const setForm = (data: Partial<CheckoutForm>) => {
    Object.assign(OrderForm.value, data);
  };

  // ✏️ Обновить одно поле
  const setField = <K extends keyof CheckoutForm>(key: K, value: CheckoutForm[K]) => {
    OrderForm.value[key] = value;
  };

  const resetForm = () => {
    const selectedAddress = storeUserProfile.getSelectedDeliveryAddress?.value;
    OrderForm.value = {
      comment: '',
      cutlery: 0,
      payment_method: 'card',
      method_receipt: 'delivery',
      table_id: 0,
      delivery_address: selectedAddress
        ? { address: selectedAddress.address, zona: selectedAddress.zona == null ? undefined : String(selectedAddress.zona) }
        : null,
      delivery_zone_id: selectedAddress?.zona == null ? null : String(selectedAddress.zona),
      pointsToUse: 0,
      promocodeId: null,
      preorder_mode: 'asap',
      reservation_time: null,
    };
  };

  watch(
    () => storeUserProfile.getUser,
    (user) => {
      if (user) {
          OrderForm.value.phone = user.default_phone_number || '';
          OrderForm.value.name = user.first_name || '';
      }
    },
    { immediate: true, deep: true },
  );
  watch(
    () => storeUserProfile.selectedDeliveryAddress,
    (selected) => {
      if (selected) {
          OrderForm.value.delivery_address = {
            address: selected.address,
            zona: selected.zona == null ? undefined : String(selected.zona),
          };
          OrderForm.value.delivery_zone_id = selected.zona == null ? null : String(selected.zona);
      } else {
        OrderForm.value.delivery_address = null;
        OrderForm.value.delivery_zone_id = null;
      }
    },
    { immediate: true, deep: true },
  );

  // Сброс выбранного времени предзаказа при СМЕНЕ филиала. Слоты привязаны к
  // филиалу (своё расписание, время готовки, загрузка курьеров), поэтому время,
  // выбранное в старом филиале, в новом может вообще отсутствовать. Возвращаем
  // выбор к «Ближайшее» (asap), чтобы не унести в заказ невалидный
  // reservation_time. Корзина мигрирует отдельно (useCartStore.migrateCartToBranch).
  watch(
    () => storeInstitution.selectedBranch?.id,
    (newBranchId, oldBranchId) => {
      if (!newBranchId || oldBranchId == null || newBranchId === oldBranchId) return;
      if (OrderForm.value.preorder_mode === 'scheduled' || OrderForm.value.reservation_time) {
        OrderForm.value.preorder_mode = 'asap';
        OrderForm.value.reservation_time = null;
      }
    },
  );

  return {
    OrderForm,
    getForm,
    setForm,
    setField,
    resetForm,
    getOrderId,
    setOrderId,
    deliveryCoords,
    setDeliveryCoords,
  };
});


